import { useMemo, useState, useEffect, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Clock } from "lucide-react";

/**
 * TimeInput12 — compact 12-hour time picker.
 *
 * Previously rendered 3 side-by-side <select> elements which overflowed on
 * narrow dialogs and showed OS-level spinner chrome. This version renders
 * ONE button (e.g. `7:00 PM`) that opens a popover with scrollable hour
 * and minute lists plus an AM/PM toggle. API is unchanged — reads/writes
 * a 24h "HH:MM" string — so every existing call site upgrades for free.
 */
export default function TimeInput12({ value, onChange, minuteStep = 5, testid, disabled, className = "" }) {
  const [open, setOpen] = useState(false);
  const parsed = useMemo(() => parse24(value), [value]);
  const minutes = useMemo(() => {
    const step = Math.max(1, Math.min(30, minuteStep));
    const out = [];
    for (let m = 0; m < 60; m += step) out.push(m);
    if (!out.includes(parsed.min)) out.push(parsed.min);
    return out.sort((a, b) => a - b);
  }, [minuteStep, parsed.min]);
  const hours = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  const push = (h12, min, period) => {
    let h24 = h12 % 12;
    if (period === "PM") h24 += 12;
    onChange?.(`${pad(h24)}:${pad(min)}`);
  };

  const label = value ? `${parsed.h12}:${pad(parsed.min)} ${parsed.period}` : "—";

  // Auto-scroll the selected hour/minute into view when the popover opens
  // so the user lands on their current choice rather than the top.
  const hRef = useRef(null);
  const mRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const scrollTo = (ref, selector) => {
      const el = ref.current?.querySelector(selector);
      if (el) el.scrollIntoView({ block: "center" });
    };
    requestAnimationFrame(() => {
      scrollTo(hRef, '[aria-selected="true"]');
      scrollTo(mRef, '[aria-selected="true"]');
    });
  }, [open]);

  return (
    <Popover open={open} onOpenChange={disabled ? () => {} : setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid || "time-input-12"}
          disabled={disabled}
          className={`h-11 w-full min-w-0 px-3 rounded-md bg-[#121212] border border-white/10 hover:border-white/20 text-left text-sm inline-flex items-center gap-2 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold disabled:opacity-50 ${className}`}
        >
          <Clock className="w-4 h-4 text-zinc-400 shrink-0" />
          <span className={value ? "text-white font-mono tabular-nums" : "text-zinc-500"}>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-[#121212] border-white/10 text-white z-[120]"
      >
        <div className="flex p-2 gap-1">
          <div ref={hRef} className="w-14 max-h-56 overflow-y-auto rounded border border-white/10 bg-[#0A0A0A]" data-testid={testid ? `${testid}-h-list` : "time-h-list"}>
            {hours.map((h) => {
              const active = h === parsed.h12;
              return (
                <button
                  key={h}
                  type="button"
                  aria-selected={active}
                  data-testid={testid ? `${testid}-h-${h}` : undefined}
                  onClick={() => push(h, parsed.min, parsed.period)}
                  className={`w-full px-2 py-1 text-sm font-mono tabular-nums text-center hover:bg-white/5 ${active ? "bg-gold/15 text-gold" : "text-zinc-200"}`}
                >
                  {h}
                </button>
              );
            })}
          </div>
          <div className="self-center text-zinc-600 text-sm">:</div>
          <div ref={mRef} className="w-14 max-h-56 overflow-y-auto rounded border border-white/10 bg-[#0A0A0A]" data-testid={testid ? `${testid}-m-list` : "time-m-list"}>
            {minutes.map((m) => {
              const active = m === parsed.min;
              return (
                <button
                  key={m}
                  type="button"
                  aria-selected={active}
                  data-testid={testid ? `${testid}-m-${m}` : undefined}
                  onClick={() => push(parsed.h12, m, parsed.period)}
                  className={`w-full px-2 py-1 text-sm font-mono tabular-nums text-center hover:bg-white/5 ${active ? "bg-gold/15 text-gold" : "text-zinc-200"}`}
                >
                  {pad(m)}
                </button>
              );
            })}
          </div>
          <div className="flex flex-col gap-1 ml-1">
            {["AM", "PM"].map((p) => {
              const active = p === parsed.period;
              return (
                <button
                  key={p}
                  type="button"
                  data-testid={testid ? `${testid}-${p.toLowerCase()}` : undefined}
                  onClick={() => push(parsed.h12, parsed.min, p)}
                  className={`px-3 py-1 rounded border text-xs font-semibold ${active ? "bg-gold text-black border-gold" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}
                >
                  {p}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-auto px-3 py-1 rounded border border-white/15 text-xs text-zinc-300 hover:bg-white/5"
            >
              Done
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function pad(n) { return String(n).padStart(2, "0"); }

function parse24(v) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || ""));
  const h24 = m ? Number(m[1]) : 0;
  const min = m ? Number(m[2]) : 0;
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return { h24, min, h12, period };
}
